import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Package, ShoppingBag, Truck, Tag, MapPin, Mail, Wallet, MessageCircle } from 'lucide-react';
import { listingsAPI, ordersAPI, offersAPI, messagesAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import MyListingsTab from '../components/dashboard/MyListingsTab';
import MyOrdersTab from '../components/dashboard/MyOrdersTab';
import MySalesTab from '../components/dashboard/MySalesTab';
import MyOffersTab from '../components/dashboard/MyOffersTab';
import MyProfileTab from '../components/dashboard/MyProfileTab';
import MyWalletTab from '../components/dashboard/MyWalletTab';
import MessagesTab from '../components/dashboard/MessagesTab';

const DashboardPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'listings';
  const [listings, setListings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [sales, setSales] = useState([]);
  const [receivedOffers, setReceivedOffers] = useState([]);
  const [sentOffers, setSentOffers] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      listingsAPI.getMine(),
      ordersAPI.getMyOrders(),
      ordersAPI.getMySales(),
      offersAPI.getReceived(),
      offersAPI.getSent(),
      messagesAPI.getUnreadCount(),
    ]).then(([l, o, s, ro, so, uc]) => {
      setListings(l.data);
      setOrders(o.data);
      setSales(s.data);
      setReceivedOffers(ro.data.offers || []);
      setSentOffers(so.data.offers || []);
      setUnreadMessages(uc.data.count || 0);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const pendingOffersCount = receivedOffers.filter(o => o.status === 'pending').length + sentOffers.filter(o => o.status === 'countered').length;

  return (
    <div className="min-h-screen bg-[#F0F7FA]">
      <div className="bg-gradient-to-r from-[#033871] to-[#0C63BF] py-5 sm:py-8">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-xl sm:text-2xl font-heading font-bold text-white">{t('dash.title')}</h1>
          <p className="text-sm text-[#B8D4E3] mt-1">{t('dash.hello', { name: user?.first_name })}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Email verification banner */}
        {user && !user.email_verified && (
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4" data-testid="email-verification-banner">
            <Mail className="w-5 h-5 text-amber-500 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800">{t('dash.verify_email')}</p>
              <p className="text-xs text-amber-600">{t('dash.code_sent')} <strong>{user.email}</strong>.</p>
            </div>
            <Link to="/verify-email" className="text-xs text-amber-700 font-bold hover:underline shrink-0" data-testid="verify-email-link">
              {t('dash.enter_code')}
            </Link>
          </div>
        )}
        <Tabs defaultValue={defaultTab}>
          <TabsList className="w-full border-b border-[#D6E8F0] bg-transparent h-auto p-0 rounded-none mb-6 grid grid-cols-4 sm:flex sm:justify-start sm:flex-nowrap overflow-x-auto">
            <TabsTrigger value="listings" data-testid="tab-listings" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0C63BF] data-[state=active]:bg-transparent px-1.5 sm:px-4 py-2.5 sm:py-3 gap-1 sm:gap-1.5 text-[10px] sm:text-sm flex-col sm:flex-row">
              <Package className="w-4 h-4" /> {t('dash.tab_listings')}
            </TabsTrigger>
            <TabsTrigger value="messages" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0C63BF] data-[state=active]:bg-transparent px-1.5 sm:px-4 py-2.5 sm:py-3 gap-1 sm:gap-1.5 text-[10px] sm:text-sm flex-col sm:flex-row relative" data-testid="tab-messages">
              <MessageCircle className="w-4 h-4" /> {t('dash.tab_messages')}
              {unreadMessages > 0 && (
                <span className="w-4 h-4 sm:w-5 sm:h-5 bg-[#0C63BF] text-white text-[9px] sm:text-[10px] font-bold rounded-full flex items-center justify-center absolute -top-0.5 -right-0.5 sm:static">{unreadMessages}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0C63BF] data-[state=active]:bg-transparent px-1.5 sm:px-4 py-2.5 sm:py-3 gap-1 sm:gap-1.5 text-[10px] sm:text-sm flex-col sm:flex-row">
              <ShoppingBag className="w-4 h-4" /> {t('dash.tab_orders')}
            </TabsTrigger>
            <TabsTrigger value="sales" data-testid="tab-sales" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0C63BF] data-[state=active]:bg-transparent px-1.5 sm:px-4 py-2.5 sm:py-3 gap-1 sm:gap-1.5 text-[10px] sm:text-sm flex-col sm:flex-row">
              <Truck className="w-4 h-4" /> {t('dash.tab_sales')}
            </TabsTrigger>
            <TabsTrigger value="offers" data-testid="tab-offers" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0C63BF] data-[state=active]:bg-transparent px-1.5 sm:px-4 py-2.5 sm:py-3 gap-1 sm:gap-1.5 text-[10px] sm:text-sm flex-col sm:flex-row">
              <Tag className="w-4 h-4" /> {t('dash.tab_offers')} {pendingOffersCount > 0 && (
                <span className="w-4 h-4 sm:w-5 sm:h-5 bg-red-500 text-white text-[9px] sm:text-[10px] font-bold rounded-full flex items-center justify-center absolute -top-0.5 -right-0.5 sm:static">
                  {pendingOffersCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="wallet" data-testid="tab-wallet" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0C63BF] data-[state=active]:bg-transparent px-1.5 sm:px-4 py-2.5 sm:py-3 gap-1 sm:gap-1.5 text-[10px] sm:text-sm flex-col sm:flex-row">
              <Wallet className="w-4 h-4" /> {t('dash.tab_wallet')}
            </TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0C63BF] data-[state=active]:bg-transparent px-1.5 sm:px-4 py-2.5 sm:py-3 gap-1 sm:gap-1.5 text-[10px] sm:text-sm flex-col sm:flex-row">
              <MapPin className="w-4 h-4" /> {t('dash.tab_profile')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="listings">
            <MyListingsTab listings={listings} setListings={setListings} loading={loading} />
          </TabsContent>

          <TabsContent value="messages">
            <MessagesTab />
          </TabsContent>

          <TabsContent value="orders">
            <MyOrdersTab orders={orders} setOrders={setOrders} />
          </TabsContent>

          <TabsContent value="sales">
            <MySalesTab sales={sales} setSales={setSales} />
          </TabsContent>

          <TabsContent value="offers">
            <MyOffersTab
              receivedOffers={receivedOffers}
              setReceivedOffers={setReceivedOffers}
              sentOffers={sentOffers}
              setSentOffers={setSentOffers}
              setListings={setListings}
            />
          </TabsContent>

          <TabsContent value="wallet">
            <MyWalletTab />
          </TabsContent>

          <TabsContent value="profile">
            <MyProfileTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DashboardPage;
